import React, { useState, useEffect, useRef } from 'react';
import { attachHereMap, detachHereMap } from '../lib/hereMap';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { TakeittoLogo } from '../components/TakeittoLogo';
import { 
  Package, MapPin, Clock, Truck, CheckCircle, 
  Navigation, Phone, RefreshCw, AlertCircle, User, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || '';
const HERE_API_KEY = process.env.REACT_APP_HERE_API_KEY;

// Status config
const statusConfig = {
  pending: { 
    label: 'Order Received', 
    color: 'bg-amber-500', 
    icon: Package,
    description: 'Your order has been received and is being prepared'
  },
  assigned: { 
    label: 'Driver Assigned', 
    color: 'bg-blue-500', 
    icon: Truck,
    description: 'A driver has been assigned to your delivery'
  },
  in_transit: { 
    label: 'Out for Delivery', 
    color: 'bg-indigo-500', 
    icon: Navigation,
    description: 'Your order is on the way!'
  },
  delivered: { 
    label: 'Delivered', 
    color: 'bg-emerald-500', 
    icon: CheckCircle,
    description: 'Your order has been delivered'
  },
  delayed: { 
    label: 'Delayed', 
    color: 'bg-red-500', 
    icon: AlertCircle,
    description: 'There is a delay with your delivery'
  }
};

// Tracking Map Component
const TrackingMap = ({ orderLocation, driverLocation }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    if (typeof window.H === 'undefined') return;
    
    const initMap = () => {
      try {
        const center = {
          lat: driverLocation?.latitude || orderLocation?.lat || -33.8688,
          lng: driverLocation?.longitude || orderLocation?.lng || 151.2093
        };
        
        const map = attachHereMap('tracking', mapRef.current, (element) => {
          const platform = new window.H.service.Platform({ apikey: HERE_API_KEY });
          const defaultLayers = platform.createDefaultLayers();
          
          const newMap = new window.H.Map(
            element,
            defaultLayers.vector.normal.map,
            { center, zoom: 14, pixelRatio: window.devicePixelRatio || 1 }
          );
          
          new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(newMap));
          return newMap;
        });
        // A map kept from an earlier visit still shows where it was left
        map.setCenter(center);
        map.setZoom(14);
        
        mapInstanceRef.current = map;
        setMapReady(true);
      } catch (err) {
        console.error('Map error:', err);
      }
    };
    
    const timeoutId = setTimeout(initMap, 500);
    
    return () => {
      clearTimeout(timeoutId);
      if (mapInstanceRef.current) detachHereMap('tracking');
      mapInstanceRef.current = null;
    };
  }, []);
  
  // Update markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    
    map.removeObjects(map.getObjects());
    
    // Add order destination marker
    if (orderLocation?.lat && orderLocation?.lng) {
      const orderIcon = new window.H.map.Icon(
        `<svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 0C8.954 0 0 8.954 0 20c0 15 20 30 20 30s20-15 20-30C40 8.954 31.046 0 20 0z" fill="#EF4444"/>
          <circle cx="20" cy="18" r="8" fill="white"/>
        </svg>`,
        { size: { w: 40, h: 50 }, anchor: { x: 20, y: 50 } }
      );
      const orderMarker = new window.H.map.Marker(
        { lat: orderLocation.lat, lng: orderLocation.lng }, 
        { icon: orderIcon }
      );
      map.addObject(orderMarker);
    }
    
    // Add driver marker
    if (driverLocation?.latitude && driverLocation?.longitude) {
      const driverIcon = new window.H.map.Icon(
        `<svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg">
          <circle cx="22" cy="22" r="20" fill="#6366F1" stroke="white" stroke-width="3"/>
          <rect x="12" y="17" width="20" height="10" rx="2" fill="none" stroke="white" stroke-width="2"/>
          <circle cx="16" cy="27" r="3" fill="none" stroke="white" stroke-width="2"/>
          <circle cx="28" cy="27" r="3" fill="none" stroke="white" stroke-width="2"/>
        </svg>`,
        { size: { w: 44, h: 44 }, anchor: { x: 22, y: 22 } }
      );
      const driverMarker = new window.H.map.Marker(
        { lat: driverLocation.latitude, lng: driverLocation.longitude }, 
        { icon: driverIcon }
      );
      map.addObject(driverMarker);
      
      // Center on driver if in transit
      map.setCenter({ lat: driverLocation.latitude, lng: driverLocation.longitude }, true);
    }
    
    // Fit bounds if both markers
    if (orderLocation?.lat && driverLocation?.latitude) {
      const rect = new window.H.geo.Rect(
        Math.max(orderLocation.lat, driverLocation.latitude),
        Math.min(orderLocation.lng, driverLocation.longitude),
        Math.min(orderLocation.lat, driverLocation.latitude),
        Math.max(orderLocation.lng, driverLocation.longitude)
      );
      map.getViewModel().setLookAtData({ bounds: rect }, true);
      map.setZoom(map.getZoom() - 0.5);
    }
  }, [orderLocation, driverLocation, mapReady]);
  
  return (
    <div ref={mapRef} className="w-full h-full rounded-2xl" style={{ minHeight: '300px' }} />
  );
};

// Progress Steps Component
const ProgressSteps = ({ currentStatus }) => {
  const steps = ['pending', 'assigned', 'in_transit', 'delivered'];
  const currentIndex = steps.indexOf(currentStatus);
  
  return (
    <div className="flex items-center justify-between w-full max-w-md mx-auto mb-8">
      {steps.map((step, index) => {
        const isCompleted = index <= currentIndex;
        const isCurrent = index === currentIndex;
        const config = statusConfig[step];
        const Icon = config.icon;
        
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isCompleted ? config.color : 'bg-gray-200'
              } ${isCurrent ? 'ring-4 ring-indigo-200' : ''}`}>
                <Icon className={`w-5 h-5 ${isCompleted ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <span className={`text-xs mt-2 text-center ${isCompleted ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                {config.label.split(' ')[0]}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-2 rounded ${index < currentIndex ? 'bg-indigo-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default function TrackingPage() {
  const { trackingCode } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  
  const fetchTracking = async () => {
    try {
      const res = await axios.get(`${API}/track/${trackingCode}`);
      setTrackingData(res.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to find order');
    } finally {
      setIsLoading(false);
    }
  };
  
  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!WS_URL || !trackingCode) return;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/tracking/${trackingCode}`);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('Tracking WebSocket connected');
          setWsConnected(true);
          // Keepalive ping
          const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
          ws.pingInterval = pingInterval;
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'driver_location') {
              // Update driver location in tracking data
              setTrackingData(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  driver: prev.driver ? {
                    ...prev.driver,
                    latitude: data.latitude,
                    longitude: data.longitude
                  } : prev.driver
                };
              });
              setLastUpdate(new Date());
            } else if (data.type === 'order_update') {
              // Refresh full tracking data on order update
              fetchTracking();
            }
          } catch (e) {
            console.error('Tracking WebSocket message error:', e);
          }
        };
        
        ws.onclose = (event) => {
          console.log('Tracking WebSocket disconnected', event.code);
          setWsConnected(false);
          if (ws.pingInterval) clearInterval(ws.pingInterval);
          // Don't reconnect if order not found (4004)
          if (event.code !== 4004) {
            setTimeout(connectWebSocket, 3000);
          }
        };
        
        ws.onerror = (error) => {
          console.error('Tracking WebSocket error:', error);
        };
      } catch (e) {
        console.error('Tracking WebSocket connection failed:', e);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
      }
    };
  }, [trackingCode]);
  
  useEffect(() => {
    fetchTracking();
    // Fallback polling every 30 seconds (WebSocket preferred when connected)
    const interval = setInterval(() => {
      if (!wsConnected) fetchTracking();
    }, 30000);
    return () => clearInterval(interval);
  }, [trackingCode, wsConnected]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Finding your order...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <TakeittoLogo size="md" className="mx-auto mb-6" />
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            Tracking code: <span className="font-mono font-bold">{trackingCode?.toUpperCase()}</span>
          </p>
        </div>
      </div>
    );
  }
  
  const status = statusConfig[trackingData?.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="tracking-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <TakeittoLogo size="sm" />
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchTracking}
            className="text-gray-500"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Tracking Code */}
        <div className="text-center mb-8">
          <p className="text-sm text-gray-500 mb-1">Tracking Code</p>
          <p className="font-mono text-2xl font-bold text-indigo-600">
            #{trackingData?.tracking_code}
          </p>
        </div>
        
        {/* Progress Steps */}
        <ProgressSteps currentStatus={trackingData?.status} />
        
        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-14 h-14 rounded-full ${status.color} flex items-center justify-center`}>
              <StatusIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{status.label}</h2>
              <p className="text-gray-600">{status.description}</p>
            </div>
          </div>
          
          {/* ETA */}
          {trackingData?.driver?.eta_minutes && trackingData?.status === 'in_transit' && (
            <div className="bg-indigo-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-600 font-medium">Estimated Arrival</p>
                  <p className="text-3xl font-bold text-indigo-700">{trackingData.driver.eta_minutes} min</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-indigo-600 font-medium">Distance</p>
                  <p className="text-2xl font-bold text-indigo-700">{trackingData.driver.distance_km} km</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Delivery Address */}
          <div className="border-t pt-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Delivery Address</p>
                <p className="font-medium text-gray-900">{trackingData?.delivery_address}</p>
              </div>
            </div>
            
            {trackingData?.delivery_time_window && (
              <div className="flex items-center gap-3 mt-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Delivery Window</p>
                  <p className="font-medium text-gray-900">{trackingData.delivery_time_window}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Driver Info */}
        {trackingData?.driver && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5 text-indigo-600" />
              Your Driver
            </h3>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="w-7 h-7 text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{trackingData.driver.name}</p>
                {trackingData.driver.phone && (
                  <p className="text-sm text-gray-500">{trackingData.driver.phone}</p>
                )}
              </div>
              {trackingData.driver.phone && (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`tel:${trackingData.driver.phone}`, '_self')}
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Call
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Delivery Proof Photo - shown when delivered */}
        {trackingData?.status === 'delivered' && trackingData?.has_delivery_photo && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6" data-testid="delivery-proof-section">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Camera className="w-5 h-5 text-emerald-600" />
              Delivery Proof Photo
            </h3>
            <div className="bg-emerald-50 rounded-xl p-4">
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${trackingData.delivery_photo_url}`}
                alt="Delivery proof"
                className="w-full max-h-80 object-contain rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${trackingData.delivery_photo_url}`, '_blank')}
                data-testid="tracking-delivery-photo"
              />
              <p className="text-emerald-700 text-sm text-center mt-3">
                Your order was delivered successfully!
              </p>
            </div>
          </div>
        )}
        
        {/* Map */}
        {(trackingData?.driver?.latitude || trackingData?.order_latitude) && (
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Navigation className="w-5 h-5 text-indigo-600" />
              Live Tracking
            </h3>
            <div className="h-[300px] rounded-xl overflow-hidden">
              <TrackingMap 
                orderLocation={{ lat: trackingData?.order_latitude, lng: trackingData?.order_longitude }}
                driverLocation={trackingData?.driver}
              />
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center flex items-center justify-center gap-2">
              <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`} />
              {wsConnected ? 'Real-time updates active' : 'Updates every 30s'} · Last: {lastUpdate?.toLocaleTimeString()}
            </p>
          </div>
        )}
        
        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>Powered by Takeitto</p>
        </div>
      </main>
    </div>
  );
}
